'use client';

import { useState, useRef, useMemo, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import dynamic from 'next/dynamic';
import { Heart, MessageCircle, Share2, Bookmark, TrendingUp, TrendingDown, Clock, Target, ShieldAlert, DollarSign, MoreHorizontal, ChevronDown, ChevronUp, UserPlus, Check, Pencil, Trash2, AlertTriangle, Eye, X, CornerDownRight, Send, Bot, Sparkles, ThumbsDown, Flame, Play } from 'lucide-react';
import { toast } from 'sonner';
import TradeEditModal from './TradeEditModal';
import TradePriceChart from './TradePriceChart';
import SignalChartPlayer from './SignalChartPlayer';
import TradeDetailModal from './TradeDetailModal';
import { createClient } from '@/lib/supabase/client';
import { formatDateTime, parseServerDate } from '@/lib/dates';
import { LevelBadge } from '@/components/XpComponents';
import VerifiedBadge, { isVerifiedTier } from '@/components/common/VerifiedBadge';
import IdeaTypeBadge from '@/components/common/IdeaTypeBadge';
import { track } from '@/lib/analytics';
import { uploadCommentImage } from '@/lib/commentImage';
import { CommentImageButton, CommentImageDraft, CommentImageView, makePasteHandler } from './CommentImageParts';
import { useLang } from '@/lib/i18n/LanguageContext';

// Live replay chart (lightweight-charts) — lazy-loaded only when a viewer taps Replay on a card.
const LiveTradeChart = dynamic(() => import('./LiveTradeChart'), { ssr: false });

async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    const supabase = createClient();
    // First try getSession (fast, from storage)
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      return { Authorization: `Bearer ${session.access_token}` };
    }
    // Fallback: refresh session from server
    const { data: { session: refreshed } } = await supabase.auth.refreshSession();
    if (refreshed?.access_token) {
      return { Authorization: `Bearer ${refreshed.access_token}` };
    }
  } catch {}
  return {};
}

// Types, interfaces, and utilities extracted to trade-types.ts
export type { TradeDirection, TradeStatus, MarketType, Timeframe, Comment, TradeCardData, TradeRevision } from './trade-types';
import type { TradeDirection, TradeStatus, MarketType, Timeframe, Comment, TradeCardData } from './trade-types';
import { marketColors, timeframeLabels, getRR, formatPrice, formatTime, buildCommentTree } from './trade-types';

// Deterministic fallback chart image generator: returns an inline SVG data URI
function getFallbackChartImage(id: string, ticker: string, direction: TradeDirection): string {
    const hash = (id + ticker).split('').reduce((a, c) => ((a << 5) - a + c.charCodeAt(0)) | 0, 0);
    const seed = Math.abs(hash);
    const isBuy = direction === 'BUY';
    const lineColor = isBuy ? '#0e8a4d' : '#d93843';
    const fillColor = isBuy ? 'rgba(14,138,77,0.18)' : 'rgba(217,56,67,0.18)';
    const W = 800, H = 360, N = 48;
    // Simple seeded PRNG (mulberry32)
    let s = seed || 1;
    const rand = () => { s |= 0; s = (s + 0x6D2B79F5) | 0; let t = Math.imul(s ^ (s >>> 15), 1 | s); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
    // Build a candlestick-like price walk
    let price = 50;
    const drift = isBuy ? 0.6 : -0.6;
  const candles: any[] = [];
  for (let i = 0; i < N; i++) {
        const o = price;
        const change = (rand() - 0.5) * 6 + drift * (i / N);
        const c = o + change;
        const h = Math.max(o, c) + rand() * 2;
        const l = Math.min(o, c) - rand() * 2;
        candles.push({ o, c, h, l });
        price = c;
  }
    // Normalize to SVG coords
    const allPrices = candles.flatMap((k: any) => [k.h, k.l]);
    const minP = Math.min.apply(null, allPrices);
    const maxP = Math.max.apply(null, allPrices);
    const pad = 30;
    const yScale = (p: number) => pad + (H - 2 * pad) * (1 - (p - minP) / (maxP - minP || 1));
    const xStep = (W - 2 * pad) / N;
    const cw = Math.max(4, xStep * 0.6);
    let bars = '';
    for (let i = 0; i < N; i++) {
          const k = candles[i];
          const cx = pad + i * xStep + xStep / 2;
          const up = k.c >= k.o;
          const col = up ? '#0e8a4d' : '#d93843';
          const yo = yScale(k.o), yc = yScale(k.c), yh = yScale(k.h), yl = yScale(k.l);
          const top = Math.min(yo, yc);
          const bh = Math.max(2, Math.abs(yc - yo));
          bars += `<line x1='${cx}' y1='${yh}' x2='${cx}' y2='${yl}' stroke='${col}' stroke-width='1.5'/><rect x='${cx - cw / 2}' y='${top}' width='${cw}' height='${bh}' fill='${col}' rx='1'/>`;
    }
    const grid = `<line x1='${pad}' y1='${H/4}' x2='${W-pad}' y2='${H/4}' stroke='#e3dfd3' stroke-width='1' stroke-dasharray='4 4'/><line x1='${pad}' y1='${H/2}' x2='${W-pad}' y2='${H/2}' stroke='#e3dfd3' stroke-width='1' stroke-dasharray='4 4'/><line x1='${pad}' y1='${3*H/4}' x2='${W-pad}' y2='${3*H/4}' stroke='#e3dfd3' stroke-width='1' stroke-dasharray='4 4'/>`;
    const lastY = yScale(candles[N-1].c);
    const lastLine = `<line x1='${pad}' y1='${lastY}' x2='${W-pad}' y2='${lastY}' stroke='${lineColor}' stroke-width='1' stroke-dasharray='2 3' opacity='0.6'/>`;
    const label = ``; const _unusedLabel = `<text x='20' y='32' font-family='monospace' font-size='20' font-weight='700' fill='#1c2b26'>${ticker}</text><text x='20' y='52' font-family='monospace' font-size='12' fill='${lineColor}'>${isBuy ? 'LONG' : 'SHORT'}</text>`;
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${W} ${H}' width='${W}' height='${H}'><rect width='100%' height='100%' fill='#f6f4ee'/>${grid}${lastLine}${bars}${label}</svg>`;
    return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);
}


interface TradeCardProps {
  trade: TradeCardData;
  compact?: boolean;
  currentUserId?: string;
  onStatusUpdated?: (tradeId: string, status: TradeStatus, outcomePrice?: number, exitedAt?: string) => void;
  onTradeDeleted?: (tradeId: string) => void;
  isHighlighted?: boolean;
}

// ── Threaded Comment Component ─────────────────────────────────────────────
interface CommentItemProps {
  comment: Comment;
  tradeId: string;
  depth?: number;
  onReply: (parentId: string, parentAuthor: string) => void;
  currentUserId?: string;
}

function CommentItem({ comment, tradeId, depth = 0, onReply, currentUserId }: CommentItemProps) {
  const { t } = useLang();
  const [liked, setLiked] = useState(comment.isLiked ?? false);
  const [likeCount, setLikeCount] = useState(comment.likeCount ?? 0);
  const [showReplies, setShowReplies] = useState(false);

  async function handleLikeComment() {
    const wasLiked = liked;
    setLiked(!wasLiked);
    setLikeCount((p) => wasLiked ? p - 1 : p + 1);
    try {
      const authHeaders = await getAuthHeaders();
      const res = await fetch(`/api/trades/${tradeId}/comments/${comment.id}/like`, {
        method: 'POST',
        headers: { ...authHeaders },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setLiked(data.action === 'liked');
        setLikeCount(data.likeCount);
      } else {
        setLiked(wasLiked);
        setLikeCount((p) => wasLiked ? p + 1 : p - 1);
      }
    } catch {
      setLiked(wasLiked);
      setLikeCount((p) => wasLiked ? p + 1 : p - 1);
    }
  }

  const replies = comment.replies ?? [];

  // ── AI Analysis Comment ──────────────────────────────────────────────────
  if (comment.isAiAnalysis) {
    // Parse structured content from AI comment
    const lines = comment.content.split('\n').filter(Boolean);
    const scoreMatch = comment.content.match(/Score:\s*(\d+)\/10/);
    const score = scoreMatch ? parseInt(scoreMatch[1]) : null;
    const scoreColor = score !== null ? (score >= 7 ? 'var(--bullish)' : score >= 5 ? 'var(--win)' : 'var(--bearish)') : 'var(--text-secondary)';
    const hasWarning = comment.content.includes('⚠️');

    return (
      <div
        className="rounded-xl overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(12,91,65,0.05), rgba(62,95,191,0.05))',
          border: '1px solid rgba(62,95,191,0.25)',
          boxShadow: '0 0 12px rgba(62,95,191,0.08)',
        }}
      >
        {/* AI Badge Header */}
        <div
          className="flex items-center justify-between px-3 py-2 border-b"
          style={{ borderColor: 'rgba(62,95,191,0.15)', background: 'rgba(62,95,191,0.08)' }}
        >
          <div className="flex items-center gap-2">
            <div
              className="w-6 h-6 rounded-lg flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, var(--accent), var(--info))' }}
            >
              <Bot size={12} style={{ color: 'white' }} />
            </div>
            <span className="text-xs font-bold" style={{ color: 'var(--info)' }}>{t('AI Analysis')}</span>
            <span
              className="text-xs px-1.5 py-0.5 rounded font-semibold"
              style={{ background: 'var(--info-muted)', color: 'var(--info)', border: '1px solid rgba(62,95,191,0.3)' }}
            >
              {t('AI-generated')}
            </span>
          </div>
          {score !== null && (
            <div className="flex items-center gap-1">
              <Sparkles size={11} style={{ color: scoreColor }} />
              <span className="text-sm font-black font-mono" style={{ color: scoreColor }}>{score}/10</span>
            </div>
          )}
        </div>

        {/* Warning banner */}
        {hasWarning && (
          <div
            className="flex items-center gap-2 px-3 py-1.5 border-b"
            style={{ background: 'var(--win-muted)', borderColor: 'rgba(176,124,31,0.25)' }}
          >
            <AlertTriangle size={11} style={{ color: 'var(--win)' }} />
            <span className="text-xs font-semibold" style={{ color: 'var(--win)' }}>
              {comment.content.match(/⚠️ \*\*Warnings:\*\* (.+)/)?.[1] || t('Setup has warnings')}
            </span>
          </div>
        )}

        {/* Content */}
        <div className="px-3 py-3 space-y-2">
          {lines
            .filter((l) => !l.includes('⚠️') && !l.includes('Score:'))
            .map((line, i) => {
              const boldMatch = line.match(/^\*\*(.+?):\*\*\s*(.+)$/);
              if (boldMatch) {
                return (
                  <div key={i} className="flex gap-2">
                    <span className="text-xs font-semibold shrink-0" style={{ color: 'var(--info)', minWidth: 0 }}>
                      {boldMatch[1].replace(/[📊📈🎯🛡️]/g, '').trim()}:
                    </span>
                    <span className="text-xs leading-relaxed" style={{ color: 'var(--text-body)' }}>{boldMatch[2]}</span>
                  </div>
                );
              }
              if (line.trim()) {
                return (
                  <p key={i} className="text-sm leading-relaxed" style={{ color: 'var(--text-body)' }}>{line}</p>
                );
              }
              return null;
            })}
        </div>

        {/* Footer */}
        <div className="px-3 py-2 border-t flex items-center justify-between" style={{ borderColor: 'var(--border-subtle)' }}>
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {t('{time} ago').replace('{time}', formatTime(comment.createdAt))}
          </span>
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('Powered by AI')}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={depth > 0 ? 'mt-2' : ''}
      style={depth > 0 ? { marginLeft: 20, paddingLeft: 16, borderLeft: '2.5px solid var(--border)' } : undefined}
    >
      <div className="flex gap-2.5">
        {/* Avatar */}
        <div className="shrink-0 relative">
          {comment.authorAvatarUrl ? (
            <img
              src={comment.authorAvatarUrl}
              alt={comment.author}
              className="w-7 h-7 rounded-full object-cover"
            />
          ) : (
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
              style={{ background: 'var(--accent-muted)', color: 'var(--accent)' }}
            >
              {comment.authorInitial}
            </div>
          )}
          {isVerifiedTier(comment.authorVerificationTier) && <VerifiedBadge size={12} />}
        </div>

        <div className="flex-1 min-w-0">
          {/* Comment bubble */}
          <div
            className="rounded-xl px-3 py-2"
            style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border-subtle)' }}
          >
            <div className="flex items-center gap-2 mb-1">
              <Link
                href={`/profile/${comment.author}`}
                className="text-xs font-semibold hover:underline"
                style={{ color: 'var(--text-primary)' }}
                onClick={(e) => e.stopPropagation()}
              >
                @{comment.author}
              </Link>
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('{time} ago').replace('{time}', formatTime(comment.createdAt))}</span>
            </div>
            {comment.content && (
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-body)' }}>{comment.content}</p>
            )}
            {comment.imageUrl && <CommentImageView url={comment.imageUrl} />}
          </div>

          {/* Comment actions */}
          <div className="flex items-center gap-3 mt-1 px-1">
            <button
              onClick={handleLikeComment}
              className="flex items-center gap-1 text-xs transition-colors duration-100"
              style={{ color: liked ? 'var(--bearish)' : 'var(--text-muted)' }}
            >
              <Heart size={11} fill={liked ? 'var(--bearish)' : 'none'} />
              {likeCount > 0 && <span>{likeCount}</span>}
            </button>
            {depth === 0 && (
              <button
                onClick={() => onReply(comment.id, comment.author)}
                className="flex items-center gap-1 text-xs transition-colors duration-100"
                style={{ color: 'var(--text-muted)' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--accent)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-muted)')}
              >
                <CornerDownRight size={11} />
                {t('Reply')}
              </button>
            )}
            {replies.length > 0 && (
              <button
                onClick={() => setShowReplies((v) => !v)}
                className="flex items-center gap-1 text-xs transition-colors duration-100"
                style={{ color: 'var(--accent)' }}
              >
                {showReplies ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                {replies.length} {replies.length === 1 ? t('reply') : t('replies')}
              </button>
            )}
          </div>

          {/* Nested replies */}
          {showReplies && replies.length > 0 && (
            <div className="mt-2 space-y-2">
              {replies.map((reply) => (
                <CommentItem
                  key={reply.id}
                  comment={reply}
                  tradeId={tradeId}
                  depth={depth + 1}
                  onReply={onReply}
                  currentUserId={currentUserId}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TradeCard({ trade, compact = false, currentUserId, onTradeDeleted, isHighlighted = false }: TradeCardProps) {
  const { t } = useLang();
  const router = useRouter();
  const [liked, setLiked] = useState(trade.isLiked);
  const [likeCount, setLikeCount] = useState(trade.likeCount);
  const [saved, setSaved] = useState(trade.isSaved);
  const [following, setFollowing] = useState(trade.author.isFollowing);
  const [followLoading, setFollowLoading] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState<Comment[]>(trade.comments);
  const [commentCount, setCommentCount] = useState(trade.commentCount);
  const [imageOpen, setImageOpen] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [likeAnimating, setLikeAnimating] = useState(false);
  const [reactions, setReactions] = useState(
    trade.reactions ?? { bullish: 0, bearish: 0, fire: 0, userReaction: null }
  );
  // Outcome is settled automatically by the market-data engine (service role);
  // owners can no longer set it by hand, so this just mirrors the server value.
  const tradeStatus = trade.status;
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [tradeData, setTradeData] = useState(trade);
  const [showPriceLevels, setShowPriceLevels] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [replyingTo, setReplyingTo] = useState<{ parentId: string; parentAuthor: string } | null>(null);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const commentInputRef = useRef<HTMLInputElement>(null);
  // Chart screenshot attached to the comment being written (uploaded on submit).
  const [commentImage, setCommentImage] = useState<File | null>(null);
  const [commentPosting, setCommentPosting] = useState(false);
  const commentImagePreview = useMemo(
    () => (commentImage ? URL.createObjectURL(commentImage) : null),
    [commentImage]
  );
  useEffect(() => {
    return () => { if (commentImagePreview) URL.revokeObjectURL(commentImagePreview); };
  }, [commentImagePreview]);

  // Tap Replay on a signal's static chart → swap to the live auto-playing replay in-card.
  const [showReplay, setShowReplay] = useState(false);

  const isAnalysis = tradeData.ideaType === 'ANALYSIS';
  const rr = getRR(tradeData.entryPrice, tradeData.stopLoss, tradeData.takeProfit1, tradeData.direction);
  const marketColor = marketColors[tradeData.market];
  const isBuy = tradeData.direction === 'BUY';
  const isOwner = !!(currentUserId && trade.author.userId && currentUserId === trade.author.userId);
  // Resolve media URL from all possible fields in priority order.
  // SIGNAL ideas without an upload render the LIVE chart (real candles + R/R zones drawn by the
  // site from the posted levels) — never an uploaded picture and never the fake seeded SVG.
  const hasStoredMedia = !!(tradeData.imageUrl || tradeData.chartImage || tradeData.thumbnailUrl);
  const useLiveChartMedia = !isAnalysis && !hasStoredMedia;
  const resolvedMediaUrl = tradeData.imageUrl || tradeData.chartImage || tradeData.thumbnailUrl ||
    (isAnalysis ? getFallbackChartImage(tradeData.id, tradeData.ticker, tradeData.direction) : null);
  const hasImage = !!(resolvedMediaUrl && !imageError) || useLiveChartMedia;
  // Real view count from the DB (incremented once per session on the idea's detail
  // page). No fabrication — an idea with no opens honestly shows 0.
  const viewCount = trade.viewCount ?? 0;

  // Build threaded comment tree
  const commentTree = buildCommentTree(comments);

  async function loadFullComments() {
    if (commentsLoaded || trade.is_demo) return;
    try {
      const res = await fetch(`/api/trades/${trade.id}/comments`);
      if (res.ok) {
        const data = await res.json();
        setComments(data.comments || []);
        setCommentsLoaded(true);
      }
    } catch { /* ignore */ }
  }

  async function handleLike() {
    const wasLiked = liked;
    setLikeAnimating(true);
    setLiked(!wasLiked);
    setLikeCount((prev) => (wasLiked ? prev - 1 : prev + 1));
    setTimeout(() => setLikeAnimating(false), 300);
    try {
      const authHeaders = await getAuthHeaders();
      const res = await fetch(`/api/trades/${trade.id}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setLikeCount(data.likeCount);
        setLiked(data.action === 'liked');
      } else {
        setLiked(wasLiked);
        setLikeCount((prev) => (wasLiked ? prev + 1 : prev - 1));
      }
    } catch {
      setLiked(wasLiked);
      setLikeCount((prev) => (wasLiked ? prev + 1 : prev - 1));
    }
  }

  async function handleFollow() {
    if (followLoading || isOwner) return;
    setFollowLoading(true);
    const newFollowing = !following;
    setFollowing(newFollowing);
    try {
      const authHeaders = await getAuthHeaders();
      const res = await fetch(`/api/users/${encodeURIComponent(trade.author.username)}/follow`, {
        method: 'POST',
        headers: { ...authHeaders },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setFollowing(data.following);
        if (data.following && trade.author.userId) track('follow_trader', { target_id: trade.author.userId });
        toast.success(data.following ? `${t('Now following')} @${trade.author.username}` : `${t('Unfollowed')} @${trade.author.username}`);
      } else if (res.status === 401) {
        setFollowing(!newFollowing);
        toast.error(t('Please sign in to follow traders'));
      } else {
        setFollowing(!newFollowing);
        toast.error(t('Failed to update follow status'));
      }
    } catch {
      setFollowing(!newFollowing);
      toast.error(t('Network error'));
    } finally {
      setFollowLoading(false);
    }
  }

  function handleSave() {
    setSaved((prev) => !prev);
    toast.success(saved ? t('Removed from saved') : t('Saved to collection'));
  }

  function handleShare() {
    // The detail route is /ideas/[id] — /trade/[id] does not exist and 404s.
    navigator.clipboard.writeText(`${window.location.origin}/ideas/${trade.id}`);
    toast.success(t('Link copied'));
  }

  // Whole-card click → the signal's own page (/ideas/[id]). Every interactive element inside
  // the card (buttons, links, live chart, comment box) calls stopPropagation, so only clicks on
  // "dead" areas — caption, header whitespace, badges, price levels — reach this handler.
  function handleCardClick(e: React.MouseEvent) {
    // Selecting text (caption, prices) ends with a click on mouseup — don't hijack it.
    if (window.getSelection()?.toString()) return;
    const url = `/ideas/${trade.id}`;
    // Honor cmd/ctrl-click "open in new tab" even though the card isn't a real anchor.
    if (e.metaKey || e.ctrlKey) { window.open(url, '_blank', 'noopener'); return; }
    router.push(url);
  }

  function handleReaction(type: 'bullish' | 'bearish' | 'fire') {
    if (reactions.userReaction !== type) track('idea_react', { item_id: trade.id, reaction_type: type });
    setReactions((prev) => {
      const isSame = prev.userReaction === type;
      return {
        ...prev,
        [type]: isSame ? Math.max(0, prev[type] - 1) : prev[type] + 1,
        ...(prev.userReaction && !isSame ? { [prev.userReaction]: Math.max(0, prev[prev.userReaction] - 1) } : {}),
        userReaction: isSame ? null : type,
      };
    });
    getAuthHeaders().then((authHeaders) => {
      fetch('/api/trades/react', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify({ tradeId: trade.id, type }),
      }).catch(() => {});
    });
  }

  function handleTradeUpdated(updated: Partial<TradeCardData>) {
    setTradeData((prev) => ({ ...prev, ...updated }));
  }

  async function handleDelete() {
    setDeleteLoading(true);
    try {
      const authHeaders = await getAuthHeaders();
      const res = await fetch(`/api/trades/${trade.id}`, {
        method: 'DELETE',
        headers: { ...authHeaders },
        credentials: 'include',
      });
      if (res.ok) {
        toast.success(t('Trade deleted'));
        setDeleteConfirmOpen(false);
        onTradeDeleted?.(trade.id);
      } else {
        const err = await res.json();
        toast.error(err.error || t('Failed to delete trade'));
      }
    } catch {
      toast.error(t('Network error — please try again'));
    } finally {
      setDeleteLoading(false);
    }
  }

  function handleStartReply(parentId: string, parentAuthor: string) {
    setReplyingTo({ parentId, parentAuthor });
    setTimeout(() => commentInputRef.current?.focus(), 100);
  }

  function cancelReply() {
    setReplyingTo(null);
    setCommentText('');
  }

  async function handleComment(e: React.FormEvent) {
    e.preventDefault();
    if (commentPosting) return;
    if (!commentText.trim() && !commentImage) return;
    const content = commentText.trim();
    const parentId = replyingTo?.parentId ?? null;
    setCommentPosting(true);

    try {
      // Upload the attached chart first (compressed client-side; the upload API
      // moderates that it really is a chart) — only then create the comment.
      let imageUrl: string | undefined;
      if (commentImage) imageUrl = await uploadCommentImage(commentImage);

      const authHeaders = await getAuthHeaders();
      const res = await fetch(`/api/trades/${trade.id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify({ content, parentId, imageUrl }),
      });
      if (res.ok) {
        const data = await res.json();
        setComments((prev) => [...prev, data.comment]);
        setCommentCount((prev) => prev + 1);
        setCommentText('');
        setCommentImage(null);
        setReplyingTo(null);
        toast.success(parentId ? t('Reply posted') : t('Comment posted'));
      } else {
        const err = await res.json();
        toast.error(err.error || t('Failed to post comment'));
      }
    } catch (err: any) {
      toast.error(err?.message || t('Network error. Please try again.'));
    } finally {
      setCommentPosting(false);
    }
  }

  // Border-only status pill per the warm-light redesign: transparent bg, 1.5px colored border,
  // colored text. OPEN=info blue, WIN=bullish green, LOSS=bearish red, PENDING/BE=gold.
  const statusConfig: Record<string, { label: string; color: string; bg: string; border: string; dot: string }> = {
    OPEN:          { label: 'OPEN',          color: 'var(--info)',    bg: 'transparent', border: 'var(--info)',    dot: '●' },
    WIN:           { label: 'HIT TP',        color: 'var(--bullish)', bg: 'transparent', border: 'var(--bullish)', dot: '✓' },
    LOSS:          { label: 'HIT SL',        color: 'var(--bearish)', bg: 'transparent', border: 'var(--bearish)', dot: '✗' },
    BE:            { label: 'CLOSED',        color: 'var(--win)',     bg: 'transparent', border: 'var(--win)',     dot: '~' },
    PENDING:       { label: 'PENDING',       color: 'var(--win)',     bg: 'transparent', border: 'var(--win)',     dot: '◌' },
    NOT_TRIGGERED: { label: 'NOT TRIGGERED', color: 'var(--text-muted)', bg: 'transparent', border: 'var(--text-dim)', dot: '∅' },
  };
  const sc = statusConfig[tradeStatus] ?? statusConfig.OPEN;

  return (
    <>
      <article
        onClick={handleCardClick}
        className="animate-fade-in overflow-hidden rounded-none sm:rounded-[20px] cursor-pointer"
        style={{
          background: 'var(--bg-card)',
          border: isHighlighted ? '1px solid var(--accent)' : '1px solid var(--border)',
          boxShadow: isHighlighted
            ? '0 0 0 2px var(--accent-glow), 0 16px 40px rgba(28,43,38,0.12)'
            : 'none',
          transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
        }}
      >
        {/* ══ 1. POST HEADER ══ */}
        <div className="flex items-center justify-between px-3 sm:px-4 pt-3 sm:pt-4 pb-2 sm:pb-3">
          <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
            <Link href={`/profile/${trade.author.username}`} onClick={(e) => e.stopPropagation()} className="shrink-0 relative">
              {trade.author.avatarUrl ? (
                <img
                  src={trade.author.avatarUrl}
                  alt={trade.author.displayName}
                  className="w-9 h-9 sm:w-11 sm:h-11 rounded-full object-cover"
                  loading="lazy"
                  style={{ border: isBuy ? '2px solid var(--bullish-muted)' : '2px solid var(--bearish-muted)' }}
                />
              ) : (
                <div
                  className="w-9 h-9 sm:w-11 sm:h-11 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold shrink-0"
                  style={{
                    background: isBuy ? 'var(--accent)' : 'var(--bearish)',
                    color: 'var(--bg-base)',
                    border: 'none',
                  }}
                >
                  {trade.author.initial}
                </div>
              )}
              {isVerifiedTier(trade.author.verificationTier) && <VerifiedBadge size={16} />}
            </Link>

            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <Link
                  href={`/profile/${trade.author.username}`}
                  className="font-bold text-sm transition-colors duration-100"
                  style={{ color: 'var(--text-primary)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--accent-hover)')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-primary)')}
                  onClick={(e) => e.stopPropagation()}
                >
                  {trade.author.displayName}
                </Link>
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>@{trade.author.username}</span>
                {(trade.author as any).level && (trade.author as any).level > 1 && (
                  <LevelBadge level={(trade.author as any).level} size="sm" />
                )}
                {/* Canonical SIGNAL/ANALYSIS marker — in the header so it's visible on EVERY card,
                    on every breakpoint and every media branch (the old pill was desktop-only). */}
                <IdeaTypeBadge ideaType={tradeData.ideaType} />
              </div>
              {/* Mini credibility stats — hidden on mobile for clean IG feel */}
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <Clock size={10} style={{ color: 'var(--text-dim)' }} />
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{formatDateTime(trade.createdAt)}</span>
                {tradeData.editedAt && (
                  <span className="text-xs italic" style={{ color: 'var(--text-muted)' }} title={`${t('Edited')} ${parseServerDate(tradeData.editedAt).toLocaleString()}`}>· {t('edited')}</span>
                )}
                {/* SL/TP moved after publishing — public ledger lives on the signal page */}
                {(tradeData.revisionCount ?? tradeData.revisions?.length ?? 0) > 0 && (
                  <Link
                    href={`/ideas/${trade.id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs font-semibold hover:underline"
                    style={{ color: 'var(--win)' }}
                    title={t('SL/TP were changed after publishing — every change is public on the signal page')}
                  >
                    · {tradeData.revisionCount ?? tradeData.revisions?.length}× {t('SL/TP changed')}
                  </Link>
                )}
                {trade.author.winRate != null && trade.author.totalTrades != null && trade.author.totalTrades >= 3 && (
                  <>
                    <span className="text-xs" style={{ color: 'var(--text-dim)' }}>·</span>
                    <span className="text-xs font-semibold" style={{ color: trade.author.winRate >= 60 ? 'var(--bullish)' : trade.author.winRate >= 40 ? 'var(--win)' : 'var(--bearish)' }}>
                      {trade.author.winRate}% WR
                    </span>
                    <span className="text-xs" style={{ color: 'var(--text-dim)' }}>·</span>
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{trade.author.totalTrades}T</span>
                    {trade.author.avgRR != null && trade.author.avgRR > 0 && (
                      <>
                        <span className="text-xs" style={{ color: 'var(--text-dim)' }}>·</span>
                        <span className="text-xs font-mono" style={{ color: trade.author.avgRR >= 2 ? 'var(--bullish)' : 'var(--win)' }}>{trade.author.avgRR.toFixed(1)}R</span>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Right: Follow + menu */}
          <div className="flex items-center gap-1.5 shrink-0 ml-2">
            {!isOwner && (
              !following ? (
                <button
                  onClick={(e) => { e.stopPropagation(); handleFollow(); }}
                  disabled={followLoading}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-all duration-150 disabled:opacity-60"
                  style={{ background: 'var(--accent-muted)', color: 'var(--accent)', border: '1px solid transparent' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(12,91,65,0.18)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--accent-muted)'; }}
                >
                  <UserPlus size={11} />
                  {t('Follow')}
                </button>
              ) : (
                <button
                  onClick={(e) => { e.stopPropagation(); handleFollow(); }}
                  disabled={followLoading}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-150 disabled:opacity-60"
                  style={{ background: 'var(--bg-sunken)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--bearish)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--bearish-muted)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-secondary)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)'; }}
                >
                  <Check size={11} />
                  {t('Following')}
                </button>
              )
            )}
            {/* Three-dot menu */}
            <div className="relative">
              <button
                className="p-2 rounded-full transition-colors duration-100"
                style={{ color: 'var(--text-muted)' }}
                onClick={(e) => { e.stopPropagation(); setShowMoreMenu((v) => !v); }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-secondary)'; (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-sunken)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)'; (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
              >
                <MoreHorizontal size={18} />
              </button>
              {showMoreMenu && (
                <div
                  className="absolute right-0 top-full mt-1 z-50 rounded-xl overflow-hidden animate-fade-in"
                  style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', boxShadow: '0 16px 40px rgba(28,43,38,0.16)', minWidth: 160 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {isOwner && (
                    <>
                      <button
                        onClick={() => { setEditModalOpen(true); setShowMoreMenu(false); }}
                        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm transition-colors duration-100"
                        style={{ color: 'var(--text-secondary)' }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-sunken)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-primary)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-secondary)'; }}
                      >
                        <Pencil size={14} /> {t('Edit caption')}
                      </button>
                      <button
                        onClick={() => { setDeleteConfirmOpen(true); setShowMoreMenu(false); }}
                        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm transition-colors duration-100"
                        style={{ color: 'var(--bearish)' }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bearish-muted)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                      >
                        <Trash2 size={14} /> {t('Delete trade')}
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => { handleShare(); setShowMoreMenu(false); }}
                    className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm transition-colors duration-100"
                    style={{ color: 'var(--text-secondary)' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-sunken)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-primary)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-secondary)'; }}
                  >
                    <Share2 size={14} /> {t('Copy link')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ══ 2. TRADE METADATA BADGES ══ (hidden on mobile — shown on detail page) */}
        <div className="hidden sm:flex items-center gap-1.5 px-4 pb-3 flex-wrap">
          <Link
            href={`/ideas/${trade.id}`}
            className="px-1 py-1 rounded-lg text-sm font-black tracking-wider font-mono transition-colors duration-150"
            style={{ background: 'transparent', color: 'var(--text-primary)', border: '1px solid transparent', letterSpacing: '0.02em' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = 'var(--accent)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = 'var(--text-primary)'; }}
            onClick={(e) => e.stopPropagation()}
          >
            {tradeData.ticker}
          </Link>
          {/* Idea-type marker now lives in the always-visible card header (IdeaTypeBadge) — no
              longer duplicated here in the desktop-only metadata row. */}
          <span
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold ${isBuy ? 'badge-long' : 'badge-short'}`}
          >
            {isBuy ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
            {isBuy ? 'LONG' : 'SHORT'}
          </span>
          <span className="px-2.5 py-1 rounded-lg text-xs font-semibold" style={{ background: marketColor.bg, color: marketColor.text, border: `1px solid ${marketColor.border}` }}>
            {tradeData.market}
          </span>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium" style={{ background: 'var(--bg-sunken)', color: 'var(--text-secondary)', border: '1px solid transparent' }}>
            <Clock size={9} />
            {timeframeLabels[tradeData.timeframe]}
          </span>
          <span className="px-2.5 py-1 rounded-full text-xs font-extrabold" style={{ background: sc.bg, color: sc.color, border: `1.5px solid ${sc.border}`, letterSpacing: '0.4px' }}>
            {sc.dot} {t(sc.label)}
          </span>
        </div>

        {/* ══ 3. CHART IMAGE ══ */}
        {useLiveChartMedia ? (
          // Signal whose server chart hasn't landed yet → the static-image player (Play → replay);
          // once the generated image exists the card falls through to the image branch below.
          <div onClick={(e) => e.stopPropagation()}>
            <SignalChartPlayer trade={tradeData} height={440} />
          </div>
        ) : resolvedMediaUrl && !imageError ? (
          !isAnalysis && showReplay ? (
            // Viewer tapped Replay → swap the static preview for the live auto-playing chart.
            <div onClick={(e) => e.stopPropagation()}>
              <LiveTradeChart trade={tradeData} height={440} autoPlay />
            </div>
          ) : (
          <div
            className="relative cursor-pointer group"
            onClick={(e) => { e.stopPropagation(); router.push(`/ideas/${trade.id}`); }}
            style={{ background: 'var(--bg-base)' }}
          >
            <div className="relative w-full overflow-hidden" style={{ paddingBottom: '53.33%' }}>
              <img
                src={resolvedMediaUrl}
                alt={`${tradeData.ticker} ${isBuy ? 'LONG' : 'SHORT'} chart analysis — ${tradeData.timeframe} timeframe`}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                loading="lazy"
                onError={() => setImageError(true)}
              />
              <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to top, rgba(6,12,20,0.65) 0%, transparent 40%)' }} />
              <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to bottom, rgba(6,12,20,0.4) 0%, transparent 30%)' }} />
              {/* Overlay badges — bottom-left */}
              <div className="absolute bottom-3 left-3 flex items-center gap-1.5">
                <span className="px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-lg text-xs font-black font-mono" style={{ background: 'rgba(0,0,0,0.7)', color: '#f1f5f9', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.12)', letterSpacing: '0.06em' }}>
                  {tradeData.ticker}
                </span>
                <span className="px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-lg text-xs font-bold" style={{ background: isBuy ? 'rgba(14,138,77,0.85)' : 'rgba(217,56,67,0.85)', color: 'white', backdropFilter: 'blur(12px)' }}>
                  {isBuy ? '▲ LONG' : '▼ SHORT'}
                </span>
                <span className="hidden sm:inline-flex px-2.5 py-1 rounded-lg text-xs font-mono" style={{ background: 'rgba(0,0,0,0.7)', color: '#94a3b8', backdropFilter: 'blur(12px)' }}>
                  {timeframeLabels[tradeData.timeframe]}
                </span>
              </div>
              {/* Status badge — top-left on mobile */}
              <div className="sm:hidden absolute top-2.5 left-2.5">
                <span className="px-2 py-0.5 rounded-lg text-xs font-bold" style={{ background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, backdropFilter: 'blur(12px)' }}>
                  {sc.dot} {t(sc.label)}
                </span>
              </div>
              {rr !== '—' && (
                <div className="absolute bottom-3 right-3">
                  <span className="px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-lg text-xs font-bold font-mono" style={{ background: 'rgba(0,0,0,0.7)', color: 'var(--win-bright)', backdropFilter: 'blur(12px)', border: '1px solid rgba(240,200,104,0.3)' }}>
                    {rr}R
                  </span>
                </div>
              )}
              {/* Replay — every SIGNAL card is replayable: tap = live auto-playing chart. */}
              {!isAnalysis && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setShowReplay(true); }}
                  aria-label={t('Play replay')}
                  className="absolute inset-0 m-auto flex items-center justify-center rounded-full transition-transform duration-150 hover:scale-110"
                  style={{ width: 48, height: 48, background: 'rgba(12,91,65,0.92)', boxShadow: '0 3px 12px rgba(0,0,0,0.45)' }}
                >
                  <Play size={20} fill="#fff" color="#fff" style={{ marginLeft: 2 }} />
                </button>
              )}
              <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden sm:flex">
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}>
                  <Eye size={13} style={{ color: 'white' }} />
                  <span className="text-xs font-medium text-white">{t('View Idea')}</span>
                </div>
              </div>
            </div>
          </div>
          )
        ) : isAnalysis ? null : (
          /* Price chart fallback — always shown when no image or image failed to load */
          <div className="px-4 pb-3">
            <TradePriceChart
              ticker={tradeData.ticker}
              entryPrice={tradeData.entryPrice}
              stopLoss={tradeData.stopLoss}
              takeProfit1={tradeData.takeProfit1}
              takeProfit2={tradeData.takeProfit2}
              takeProfit3={tradeData.takeProfit3}
              direction={tradeData.direction}
            />
          </div>
        )}

        {/* ══ 4. MOBILE ACTION ROW (Instagram-style) ══ */}
        <div className="sm:hidden flex items-center justify-between px-3 py-2">
          <div className="flex items-center gap-0.5">
            {/* Like */}
            <button
              onClick={(e) => { e.stopPropagation(); handleLike(); }}
              className="p-2 rounded-full transition-all duration-150 active:scale-90"
              style={{ color: liked ? 'var(--bearish)' : 'var(--text-primary)' }}
            >
              <Heart size={22} fill={liked ? 'var(--bearish)' : 'none'} strokeWidth={1.8} />
            </button>
            {/* Dislike (bearish) */}
            <button
              onClick={(e) => { e.stopPropagation(); handleReaction('bearish'); }}
              className="p-2 rounded-full transition-all duration-150 active:scale-90"
              style={{ color: reactions.userReaction === 'bearish' ? 'var(--bearish)' : 'var(--text-primary)' }}
            >
              <ThumbsDown size={20} fill={reactions.userReaction === 'bearish' ? 'var(--bearish)' : 'none'} strokeWidth={1.8} />
            </button>
            {/* Fire */}
            <button
              onClick={(e) => { e.stopPropagation(); handleReaction('fire'); }}
              className="p-2 rounded-full transition-all duration-150 active:scale-90"
              style={{ color: reactions.userReaction === 'fire' ? 'var(--win)' : 'var(--text-primary)' }}
            >
              <Flame size={21} fill={reactions.userReaction === 'fire' ? 'var(--win)' : 'none'} strokeWidth={1.8} />
            </button>
            {/* Comment */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                const willShow = !showComments;
                setShowComments(willShow);
                if (willShow) { loadFullComments(); setTimeout(() => commentInputRef.current?.focus(), 100); }
              }}
              className="p-2 rounded-full transition-all duration-150 active:scale-90"
              style={{ color: showComments ? 'var(--accent)' : 'var(--text-primary)' }}
            >
              <MessageCircle size={21} strokeWidth={1.8} />
            </button>
          </div>
          <div className="flex items-center gap-0.5">
            {/* Save */}
            <button
              onClick={(e) => { e.stopPropagation(); handleSave(); }}
              className="p-2 rounded-full transition-all duration-150 active:scale-90"
              style={{ color: saved ? 'var(--accent)' : 'var(--text-primary)' }}
            >
              <Bookmark size={21} fill={saved ? 'var(--accent)' : 'none'} strokeWidth={1.8} />
            </button>
            {/* Share */}
            <button
              onClick={(e) => { e.stopPropagation(); handleShare(); }}
              className="p-2 rounded-full transition-all duration-150 active:scale-90"
              style={{ color: 'var(--text-primary)' }}
            >
              <Share2 size={20} strokeWidth={1.8} />
            </button>
          </div>
        </div>

        {/* Mobile like count */}
        {likeCount > 0 && (
          <div className="sm:hidden px-4 pb-1">
            <span className="text-xs font-bold" style={{ color: 'var(--text-primary)' }}>{likeCount.toLocaleString()} {likeCount === 1 ? t('like') : t('likes')}</span>
          </div>
        )}

        {/* ══ 4. CAPTION ══ */}
        {/* Desktop: full caption */}
        {tradeData.caption && (
          <div className="hidden sm:block px-4 py-3">
            <p className="text-sm leading-relaxed" style={{ color: 'var(--text-body)' }}>
              <span className="font-bold mr-1" style={{ color: 'var(--text-primary)' }}>@{trade.author.username}</span>
              {tradeData.caption}
            </p>
          </div>
        )}
        {/* Mobile: 1-line truncated caption with "... more" */}
        {tradeData.caption && (
          <div className="sm:hidden px-4 pb-3 pt-1">
            <p className="text-sm" style={{ color: 'var(--text-body)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical' }}>
              <span className="font-bold mr-1" style={{ color: 'var(--text-primary)' }}>@{trade.author.username}</span>
              {tradeData.caption}
            </p>
            <Link
              href={`/ideas/${trade.id}`}
              className="text-xs font-medium mt-0.5 inline-block"
              style={{ color: 'var(--text-muted)' }}
              onClick={(e) => e.stopPropagation()}
            >
              {t('... more')}
            </Link>
          </div>
        )}

        {/* Order type + pending trigger (visible on all breakpoints) */}
        <div className="px-4 pb-2 flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase" style={{ background: 'var(--bg-sunken)', color: 'var(--text-secondary)' }}>
            {String(tradeData.orderType || 'MARKET').toUpperCase()}
          </span>
          {tradeData.status === 'PENDING'
            && ['LIMIT', 'STOP'].includes(String(tradeData.orderType || '').toUpperCase())
            && tradeData.triggerPrice != null && (
            <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: 'var(--win-muted)', color: 'var(--win)' }}>
              ◌ {t('Pending — activates at')} {formatPrice(tradeData.triggerPrice)}
            </span>
          )}
        </div>

        {/* ══ 5. PRICE LEVELS ══ (hidden on mobile; signals only — analyses have no entry/SL/TP) */}
        {!isAnalysis && (
        <div className="hidden sm:block px-4 pb-3">
          <button
            onClick={(e) => { e.stopPropagation(); setShowPriceLevels((v) => !v); }}
            className="flex items-center gap-1.5 text-xs transition-colors duration-100"
            style={{ color: 'var(--text-secondary)' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--accent)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-secondary)')}
          >
            {showPriceLevels ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {t('Price Levels')}
          </button>
          {showPriceLevels && (() => {
            const ot = String(tradeData.orderType || 'MARKET').toUpperCase();
            const isPendingOrder = tradeData.status === 'PENDING' && ['LIMIT', 'STOP'].includes(ot);
            // Once filled/settled, show the REAL anchored fill so the displayed entry matches settlement.
            const showFill = tradeData.status !== 'PENDING' && tradeData.fillEntry != null;
            const entryValue = showFill && tradeData.fillEntry != null ? tradeData.fillEntry : tradeData.entryPrice;
            const entryLabel = isPendingOrder ? 'Trigger' : 'Entry';
            const entryTitle = isPendingOrder
              ? t('Order activates (fills) at this price')
              : ot === 'MARKET' && showFill
                ? t('Entry = market price at the moment the idea was posted')
                : t('Entry price');
            return (
            <div className="mt-2 grid grid-cols-3 gap-2 animate-fade-in">
              {[
                { label: entryLabel, value: entryValue, color: 'var(--win)', icon: <DollarSign size={10} />, title: entryTitle },
                { label: 'Stop Loss', value: tradeData.stopLoss, color: 'var(--bearish)', icon: <ShieldAlert size={10} />, title: 'Stop loss' },
                { label: 'Take Profit', value: tradeData.takeProfit1, color: 'var(--bullish)', icon: <Target size={10} />, title: 'Take profit' },
              ].map((level) => (
                <div key={level.label} title={level.title} className="rounded-xl p-2.5 text-center" style={{ background: 'var(--bg-base)', border: '1px solid var(--border)' }}>
                  <div className="flex items-center justify-center gap-1 mb-1" style={{ color: level.color }}>
                    {level.icon}
                    <span className="text-xs font-medium">{level.label}</span>
                  </div>
                  <div className="text-xs font-mono font-bold" style={{ color: level.color }}>{formatPrice(level.value)}</div>
                </div>
              ))}
            </div>
            );
          })()}
        </div>
        )}

        {/* ══ 6. REACTIONS ══ (desktop only — merged into mobile action row) */}
        <div className="hidden sm:flex px-4 pb-3 items-center gap-2 flex-wrap">
          {(['bullish', 'bearish', 'fire'] as const).map((type) => {
            const emoji = type === 'bullish' ? '👍' : type === 'bearish' ? '👎' : '🔥';
            const count = reactions[type];
            const isActive = reactions.userReaction === type;
            return (
              <button
                key={type}
                onClick={(e) => { e.stopPropagation(); handleReaction(type); }}
                className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs transition-all duration-150"
                style={{
                  background: isActive ? 'var(--win-muted)' : 'transparent',
                  border: isActive ? '1px solid var(--win)' : '1px solid var(--border)',
                  color: isActive ? 'var(--win)' : 'var(--text-secondary)',
                }}
              >
                <span>{emoji}</span>
                {count > 0 && <span className="font-mono">{count}</span>}
              </button>
            );
          })}
        </div>

        {/* ══ 7. ACTION BAR ══ (desktop only — merged into mobile action row) */}
        <div className="hidden sm:flex items-center justify-between px-4 pb-3 border-t pt-3" style={{ borderColor: 'var(--border-subtle)' }}>
          <div className="flex items-center gap-1">
            {/* Like */}
            <button
              onClick={(e) => { e.stopPropagation(); handleLike(); }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm transition-all duration-150"
              style={{ color: liked ? 'var(--bearish)' : 'var(--text-primary)', background: liked ? 'var(--bearish-muted)' : 'transparent' }}
              onMouseEnter={(e) => { if (!liked) (e.currentTarget as HTMLButtonElement).style.background = 'rgba(217,56,67,0.08)'; }}
              onMouseLeave={(e) => { if (!liked) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
            >
              <Heart size={18} fill={liked ? 'var(--bearish)' : 'none'} strokeWidth={1.8} style={{ transition: 'all 150ms ease' }} />
              <span className="text-xs font-mono font-semibold">{likeCount}</span>
            </button>

            {/* Comment */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                const willShow = !showComments;
                setShowComments(willShow);
                if (willShow) {
                  loadFullComments();
                  setTimeout(() => commentInputRef.current?.focus(), 100);
                }
              }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm transition-all duration-150"
              style={{ color: showComments ? 'var(--accent)' : 'var(--text-primary)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--accent-muted)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--accent)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = showComments ? 'var(--accent)' : 'var(--text-primary)'; }}
            >
              <MessageCircle size={18} strokeWidth={1.8} />
              <span className="text-xs font-mono font-semibold">{commentCount}</span>
            </button>

            {/* Save */}
            <button
              onClick={(e) => { e.stopPropagation(); handleSave(); }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm transition-all duration-150"
              style={{ color: saved ? 'var(--accent)' : 'var(--text-primary)', background: saved ? 'var(--accent-muted)' : 'transparent' }}
              onMouseEnter={(e) => { if (!saved) (e.currentTarget as HTMLButtonElement).style.background = 'rgba(12,91,65,0.06)'; }}
              onMouseLeave={(e) => { if (!saved) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
            >
              <Bookmark size={18} fill={saved ? 'var(--accent)' : 'none'} strokeWidth={1.8} />
            </button>

            {/* Share */}
            <button
              onClick={(e) => { e.stopPropagation(); handleShare(); }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm transition-all duration-150"
              style={{ color: 'var(--text-primary)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-sunken)'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-primary)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-primary)'; }}
            >
              <Share2 size={18} strokeWidth={1.8} />
            </button>
          </div>

          {/* Views counter */}
          <div className="flex items-center gap-1.5">
            <Eye size={13} style={{ color: 'var(--text-muted)' }} />
            <span className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>
              {viewCount >= 1000 ? `${(viewCount / 1000).toFixed(1)}k` : viewCount}
            </span>
          </div>
        </div>

        {/* ══ 8. THREADED COMMENTS SECTION ══ */}
        {showComments && (
          /* stopPropagation: typing/liking/replying in the comment thread must never trigger the
             whole-card navigation to /ideas/[id]. */
          <div className="px-4 pb-4 pt-2 border-t animate-fade-in" style={{ borderColor: 'var(--border-subtle)' }} onClick={(e) => e.stopPropagation()}>
            {/* Reply indicator */}
            {replyingTo && (
              <div className="flex items-center justify-between mb-2 px-3 py-1.5 rounded-lg" style={{ background: 'var(--accent-muted)', border: '1px solid transparent' }}>
                <span className="text-xs" style={{ color: 'var(--accent)' }}>
                  <CornerDownRight size={11} className="inline mr-1" />
                  {t('Replying to')} <strong>@{replyingTo.parentAuthor}</strong>
                </span>
                <button onClick={cancelReply} className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  <X size={12} />
                </button>
              </div>
            )}

            {/* Comment input */}
            <form onSubmit={handleComment} className="flex gap-2 mb-4">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style={{ background: 'var(--accent)', color: 'var(--bg-base)' }}>
                {currentUserId ? currentUserId[0].toUpperCase() : 'A'}
              </div>
              <div className="flex-1">
                {commentImagePreview && (
                  <CommentImageDraft
                    preview={commentImagePreview}
                    uploading={commentPosting}
                    onRemove={() => setCommentImage(null)}
                  />
                )}
                <div className="flex gap-2">
                  <input
                    ref={commentInputRef}
                    type="text"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onPaste={makePasteHandler(setCommentImage)}
                    placeholder={replyingTo ? `${t('Reply to')} @${replyingTo.parentAuthor}…` : t('Add your analysis…')}
                    className="input-base flex-1 px-3 py-2 text-sm"
                  />
                  <CommentImageButton disabled={commentPosting} onPick={setCommentImage} />
                  <button
                    type="submit"
                    disabled={(!commentText.trim() && !commentImage) || commentPosting}
                    className="btn-primary px-3 py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <Send size={13} />
                    <span className="hidden sm:inline">{commentPosting ? t('Posting…') : t('Post')}</span>
                  </button>
                </div>
              </div>
            </form>

            {/* Comment tree */}
            <div className="flex flex-col gap-3">
              {commentTree.length === 0 ? (
                <p className="text-xs text-center py-4" style={{ color: 'var(--text-muted)' }}>{t('No comments yet. Share your analysis.')}</p>
              ) : (
                commentTree.map((c) => (
                  <CommentItem
                    key={c.id}
                    comment={c}
                    tradeId={trade.id}
                    depth={0}
                    onReply={handleStartReply}
                    currentUserId={currentUserId}
                  />
                ))
              )}
            </div>
          </div>
        )}
      </article>

      {/* ══ MODALS ══ */}
      <TradeEditModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        trade={tradeData}
        onTradeUpdated={handleTradeUpdated}
      />
      {deleteConfirmOpen && (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center p-4 animate-fade-in"
          style={{ background: 'rgba(28,43,38,0.6)', backdropFilter: 'blur(6px)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setDeleteConfirmOpen(false); }}
        >
          <div
            className="w-full max-w-sm animate-slide-up"
            style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 20, boxShadow: '0 24px 80px rgba(28,43,38,0.25)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 flex flex-col items-center gap-4 text-center">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--bearish-muted)', border: '1px solid transparent' }}>
                <AlertTriangle size={22} style={{ color: 'var(--bearish)' }} />
              </div>
              <div>
                <h3 className="font-bold text-base mb-1" style={{ color: 'var(--text-primary)' }}>{t('Delete Trade?')}</h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {t('This will permanently delete your')} <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{trade.ticker}</span> {t('trade idea.')}
                </p>
              </div>
              <div className="flex gap-3 w-full pt-1">
                <button type="button" onClick={() => setDeleteConfirmOpen(false)} disabled={deleteLoading} className="btn-ghost px-4 py-2.5 text-sm flex-1">{t('Cancel')}</button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleteLoading}
                  className="px-4 py-2.5 text-sm flex-1 flex items-center justify-center gap-2 rounded-xl font-semibold transition-all duration-150 disabled:opacity-60"
                  style={{ background: 'var(--bearish-muted)', color: 'var(--bearish)', border: '1px solid transparent' }}
                >
                  {deleteLoading ? <><span className="w-3.5 h-3.5 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />{t('Deleting…')}</> : <><Trash2 size={13} />{t('Delete')}</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <TradeDetailModal
        trade={{ ...trade, status: tradeStatus, comments }}
        isOpen={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        currentUserId={currentUserId}
      />

      {/* Full-screen image lightbox */}
      {imageOpen && resolvedMediaUrl && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center animate-fade-in"
          style={{ background: 'rgba(0,0,0,0.95)', backdropFilter: 'blur(12px)' }}
          onClick={() => setImageOpen(false)}
        >
          <img
            src={resolvedMediaUrl}
            alt={`${tradeData.ticker} chart analysis`}
            className="max-w-full max-h-full object-contain"
            style={{ maxWidth: 'min(95vw, 1200px)', maxHeight: '90vh', borderRadius: 12, boxShadow: '0 32px 80px rgba(0,0,0,0.9)', display: 'block' }}
            loading="lazy"
            onClick={(e) => e.stopPropagation()}
            onError={() => setImageOpen(false)}
          />
          <button
            className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-150"
            style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.15)' }}
            onClick={() => setImageOpen(false)}
          >
            <X size={18} />
          </button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <span className="text-sm font-black font-mono" style={{ color: '#f1f5f9', letterSpacing: '0.06em' }}>{tradeData.ticker}</span>
            <span className="text-xs" style={{ color: '#9ca3af' }}>·</span>
            <span className="text-xs font-bold" style={{ color: isBuy ? 'var(--bullish)' : 'var(--bearish)' }}>{isBuy ? '▲ LONG' : '▼ SHORT'}</span>
            <span className="text-xs" style={{ color: '#9ca3af' }}>·</span>
            <span className="text-xs" style={{ color: '#9ca3af' }}>{timeframeLabels[tradeData.timeframe]}</span>
          </div>
        </div>
      )}
    </>
  );
}
